// Everything the rcobchod cards render, derived from the product (CARD.md "Field mapping").
//
// Kept out of the .tsx cards so the data mapping is testable under `pnpm test` (Node type stripping
// cannot run .tsx): value imports are `@raventic/sdk/helpers` only, the SDK is type-only. A field
// whose source param is missing comes back null / empty, and the card omits its element.

import type { CardProps } from "@raventic/sdk";
import { displayText, nbsp, param, params } from "@raventic/sdk/helpers";

type Product = CardProps["product"];
type Format = CardProps["format"];

// Label next to "Do košíku" when the product has no `detail-button` param (every product has it today).
const DEFAULT_DETAIL_LABEL = "Zobrazit detail";

// One `label` param = one tag (multi-valued: the params repeat). `className` is the site's own color
// class: the label text slugged (`Výhodný set` → `vyhodny-set`), styled by rcobchod's stitky_color.css.
export type RcLabel = { text: string; className: string };

// Coupon box (`coupon_code` / `coupon` + the price after it): "<strong>2542 Kč</strong> s kódem DNYRMT".
export type RcCoupon = { price: string; code: string };

// `in-stock`: any "Skladem…" text → the site's `dostupnost skladem` (green); `other` ("Na dotaz", …)
// → our own modifier (CARD.md "Availability").
export type RcAvailabilityTone = "in-stock" | "other";
export type RcAvailability = { text: string; tone: RcAvailabilityTone };

export type RcCardView = {
  // Our product id is the shop's own id (`addId`, the compare list's `data-product-id`).
  id: string;
  name: string;
  imageUrl: string;
  labels: RcLabel[];
  // The `benefit` params (the site's description bullets), data order.
  benefits: string[];
  coupon: RcCoupon | null;
  // The selling price; with a sale, the price before it and the discount % too (CARD.md "Prices").
  price: string | null;
  oldPrice: string | null;
  discountPercent: string | null;
  availability: RcAvailability | null;
  detailLabel: string;
};

// `Plně funkční` → `plne-funkcni`, as the site derives its label classes (lowercase, diacritics
// stripped, spaces → hyphens).
export const labelClass = (text: string): string =>
  text
    .toLocaleLowerCase("cs")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .trim()
    .replace(/\s+/g, "-");

// The `label` params in data order; empty and repeated ones dropped.
export const buildLabels = (product: Product): RcLabel[] => {
  const seen = new Set<string>();
  const labels: RcLabel[] = [];
  for (const text of params(product, "label")) {
    const className = labelClass(text);
    if (className === "" || seen.has(className)) continue;
    seen.add(className);
    labels.push({ text, className });
  }
  return labels;
};

// The feed names the code param either way: `coupon` (DNYRMT, UKLID33, UKLID50, DNYRC10) or
// `coupon_code` (OUTLET30). The parser is being changed to write every code to `coupon`; `coupon_code`
// stays until no product has it (CARD.md "Coupon"). Blank values count as missing.
const COUPON_CODE_PARAMS = ["coupon_code", "coupon"];

const couponCode = (product: Product): string | null => {
  for (const name of COUPON_CODE_PARAMS) {
    const code = displayText(param(product, name));
    if (code !== null) return code;
  }
  return null;
};

// The selling price: `_sale-price` when the product is on sale, else `_price`. Since the feed got
// the sale price (2026-09-30), `_price` on a sale product is the price BEFORE the sale.
const sellingPrice = (product: Product): string | null => param(product, "_sale-price") ?? param(product, "_price");

export type RcPrices = { price: string | null; oldPrice: string | null; discountPercent: string | null };

// The site's `.cena`: the struck-through price before the sale (`.event`), then the selling price;
// its discount badge (`.tagsTop .procenta`, "30 %"). Old price and badge only for a real discount
// (sale price below `_price`).
export const buildPrices = (product: Product, format: Format): RcPrices => {
  const price = param(product, "_price");
  const sale = param(product, "_sale-price");
  const selling = sellingPrice(product);
  const onSale = price !== null && sale !== null && Number(sale) > 0 && Number(sale) < Number(price);
  return {
    price: selling === null ? null : format.priceFormatter(selling),
    oldPrice: onSale ? format.priceFormatter(price) : null,
    discountPercent: onSale ? nbsp(`${format.discountCalculator(price, sale)} %`) : null,
  };
};

// The price after the coupon, as the live pages print it (2026-09-30): the SELLING price less
// `coupon_value` percent, to the nearest crown with halves rounded DOWN (467 − 30 % → 327, 2099 − 30 %
// → 1469, 2990 − 15 % → 2541, 2090 − 15 % → 1776). `coupon_price` only when there is no
// `coupon_value`: the feed rounds halves up (2542, 1777), and on some sale products (OUTLET30) prints
// it off the price before the sale (78220: "629 Kč", the page 327 Kč; CARD.md "Coupon").
export const couponAmount = (price: number, percent: number): number => Math.ceil((price * (100 - percent)) / 100 - 0.5);

const couponPrice = (product: Product, format: Format): string | null => {
  const price = Number(sellingPrice(product));
  const percent = Number(param(product, "coupon_value"));
  if (price > 0 && percent > 0 && percent < 100) return format.priceFormatter(String(couponAmount(price, percent)));
  return displayText(param(product, "coupon_price"));
};

// Only with both the code and the price after the discount: a code alone (`DNYRC10` carries neither
// `coupon_price` nor `coupon_value` in the feed) says nothing the visitor can act on in a listing.
export const buildCoupon = (product: Product, format: Format): RcCoupon | null => {
  const code = couponCode(product);
  const price = code === null ? null : couponPrice(product, format);
  return code === null || price === null ? null : { price: nbsp(price), code };
};

export const availabilityTone = (text: string): RcAvailabilityTone => (/^skladem/i.test(text) ? "in-stock" : "other");

// `stock_text` as the site prints it. Not `_in-stock`: it is missing on some "Skladem" products and
// "true" on "Na dotaz" ones.
export const buildAvailability = (product: Product): RcAvailability | null => {
  const text = displayText(param(product, "stock_text"));
  return text === null ? null : { text, tone: availabilityTone(text) };
};

// The `benefit` params: the bullets the site shows under the title (`.popis`). Blank and repeated
// ones dropped.
export const buildBenefits = (product: Product): string[] => {
  const benefits: string[] = [];
  for (const value of params(product, "benefit")) {
    const text = displayText(value);
    if (text !== null && !benefits.includes(text)) benefits.push(text);
  }
  return benefits;
};

export const buildCardView = (product: Product, format: Format): RcCardView => ({
  id: product.id,
  name: product.name,
  imageUrl: format.imageUrlHelper(product),
  labels: buildLabels(product),
  benefits: buildBenefits(product),
  coupon: buildCoupon(product, format),
  ...buildPrices(product, format),
  availability: buildAvailability(product),
  detailLabel: param(product, "detail-button") ?? DEFAULT_DETAIL_LABEL,
});
